import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

import type { BrandSummary } from "@mongmee/contracts";

import { apiRequest } from "./api";
import { useLocation, useNavigate } from "./router";

interface ActiveBrandValue {
  activeBrand: BrandSummary | null;
  activeBrandId: string;
  brands: BrandSummary[];
  error: Error | null;
  isLoading: boolean;
  setActiveBrandId: (brandId: string) => void;
}

const ActiveBrandContext = createContext<ActiveBrandValue | null>(null);

export function ActiveBrandProvider({
  children,
  tenantId,
}: PropsWithChildren<{ tenantId: string }>) {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["brands", tenantId],
    queryFn: () =>
      apiRequest<{ brands: BrandSummary[] }>(`/v1/tenants/${tenantId}/brands`),
    enabled: Boolean(tenantId),
  });
  const brands = useMemo(
    () => (query.data?.brands ?? []).filter((brand) => brand.status === "active"),
    [query.data],
  );
  const requestedBrandId = useMemo(
    () => new URLSearchParams(location.search).get("brand") ?? "",
    [location.search],
  );
  const activeBrand = brands.find((brand) => brand.id === requestedBrandId) ?? brands[0] ?? null;

  const setActiveBrandId = useCallback((brandId: string) => {
    const selected = brands.find((brand) => brand.id === brandId);
    if (!selected) return;
    const search = new URLSearchParams(location.search);
    search.set("brand", selected.id);
    navigate(`${location.pathname}?${search.toString()}`, { replace: true });
  }, [brands, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (query.isLoading || !activeBrand || activeBrand.id === requestedBrandId) return;
    setActiveBrandId(activeBrand.id);
  }, [activeBrand, query.isLoading, requestedBrandId, setActiveBrandId]);

  const value = useMemo<ActiveBrandValue>(() => ({
    activeBrand,
    activeBrandId: activeBrand?.id ?? "",
    brands,
    error: query.error,
    isLoading: query.isLoading,
    setActiveBrandId,
  }), [activeBrand, brands, query.error, query.isLoading, setActiveBrandId]);

  return <ActiveBrandContext.Provider value={value}>{children}</ActiveBrandContext.Provider>;
}

export function useActiveBrand(): ActiveBrandValue {
  const value = useContext(ActiveBrandContext);
  if (!value) throw new Error("ActiveBrandProvider is missing.");
  return value;
}

export function useOptionalActiveBrand(): ActiveBrandValue | null {
  return useContext(ActiveBrandContext);
}
